/**
 * CreateClientAccessModal - 2 modes pour donner acces au client :
 * 1) "Lien magique" (recommande) : envoi d'un lien d'invitation 7 jours
 *    -> le client clique, choisit son mot de passe et est connecte automatiquement
 * 2) "Mot de passe direct" : password 10 chars genere visible cote interne
 */
import React, { useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CreateClientAccessModal = ({ open, onClose, defaultEmail, defaultName, leadId, projectId, onCreated }) => {
  const [mode, setMode] = useState('magic'); // 'magic' | 'password'
  const [email, setEmail] = useState(defaultEmail || '');
  const [name, setName] = useState(defaultName || '');
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState({ password: false, link: false });

  React.useEffect(() => {
    if (open) {
      setEmail(defaultEmail || '');
      setName(defaultName || '');
      setSendEmail(true);
      setResult(null);
      setError('');
      setMode('magic');
    }
  }, [open, defaultEmail, defaultName]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      if (mode === 'magic') {
        // Lien magique = invitation
        const r = await axios.post(`${API}/invitations`, {
          email: email.trim(),
          client_name: name.trim(),
          lead_id: leadId || null,
          project_id: projectId || null,
        });
        setResult({ ...r.data, mode: 'magic' });
      } else {
        // Mot de passe direct
        const r = await axios.post(`${API}/admin/client-access`, {
          email: email.trim(),
          name: name.trim(),
          lead_id: leadId || null,
          project_id: projectId || null,
          send_email: sendEmail,
        });
        setResult({ ...r.data, mode: 'password' });
      }
      if (onCreated) onCreated();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur creation acces');
    } finally { setBusy(false); }
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [key]: true });
      setTimeout(() => setCopied({ password: false, link: false }), 2000);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="create-client-access-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">
            {result ? 'Acces cree avec succes' : 'Creer un acces client'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-modal">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {!result ? (
          <div className="space-y-4">
            {/* Mode toggle */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg" data-testid="mode-toggle">
              <button onClick={() => setMode('magic')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold transition-all ${mode === 'magic' ? 'bg-white shadow text-[#008A8D]' : 'text-gray-500'}`}
                      data-testid="mode-magic">
                Lien magique
              </button>
              <button onClick={() => setMode('password')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold transition-all ${mode === 'password' ? 'bg-white shadow text-[#A57F4F]' : 'text-gray-500'}`}
                      data-testid="mode-password">
                Mot de passe direct
              </button>
            </div>

            <p className="text-xs text-gray-500 italic">
              {mode === 'magic'
                ? "Le client recoit un email avec un lien d'invitation valable 7 jours. Il choisit lui-meme son mot de passe."
                : "Un mot de passe de 10 caracteres est genere maintenant. Vous pouvez le copier et l'envoyer manuellement."}
            </p>

            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Nom du client</label>
              <input value={name} onChange={e => setName(e.target.value)}
                     placeholder="Marie Dupont"
                     className="realease-input"
                     data-testid="client-access-name" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Email du client</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                     placeholder="marie@exemple.fr"
                     className="realease-input"
                     data-testid="client-access-email" />
            </div>
            {mode === 'password' && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)}
                       className="w-4 h-4 accent-[#008A8D]"
                       data-testid="client-access-send-email" />
                <span className="text-gray-700">Envoyer les identifiants par email au client</span>
              </label>
            )}
            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="client-access-error">{error}</p>
            )}
            <div className="flex gap-3 pt-2">
              <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 font-medium" data-testid="client-access-cancel">Annuler</button>
              <button onClick={submit} disabled={busy || !email.trim() || !name.trim()}
                      className={`flex-1 px-4 py-2 text-white rounded-lg font-semibold disabled:opacity-50 ${mode === 'magic' ? 'bg-[#008A8D] hover:bg-[#006B6D]' : 'bg-[#A57F4F] hover:bg-[#8B6A40]'}`}
                      data-testid="client-access-submit">
                {busy ? 'Creation...' : (mode === 'magic' ? 'Envoyer le lien' : "Creer l'acces")}
              </button>
            </div>
          </div>
        ) : result.mode === 'magic' ? (
          <div className="space-y-4">
            {result.email_sent ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-semibold mb-1 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  Email envoye au client
                </p>
                <p className="text-xs text-green-700">L'email d'invitation a ete livre a <b>{result.email}</b>. Lien valable 7 jours.</p>
                <p className="text-xs text-green-600 mt-1">Si le client ne le voit pas, demandez-lui de verifier ses spams ou utilisez le lien ci-dessous.</p>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg" data-testid="email-not-sent-warning">
                <p className="text-sm text-amber-900 font-bold mb-1 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                  L'email n'a PAS pu etre envoye
                </p>
                <p className="text-xs text-amber-800">L'invitation est bien creee, mais l'email automatique a echoue. <b>Copiez le lien ci-dessous et envoyez-le au client manuellement</b> (SMS, WhatsApp, ou nouveau mail depuis votre messagerie).</p>
                {result.email_error && <p className="text-[10px] text-amber-700 italic mt-1 font-mono">Raison : {result.email_error}</p>}
              </div>
            )}
            <div className="p-4 bg-[#008A8D]/5 border border-[#008A8D]/20 rounded-lg space-y-2">
              <p className="text-xs font-bold text-[#006B6E] uppercase tracking-wide mb-1">Lien magique du client</p>
              <div className="flex items-center gap-2">
                <p className="text-xs flex-1 font-mono bg-white px-2 py-2 rounded border border-[#008A8D]/20 break-all" data-testid="magic-link-url">{result.magic_link}</p>
                <button onClick={() => copy('link', result.magic_link)}
                        className="px-3 py-2 text-xs font-semibold border border-[#008A8D] text-[#008A8D] rounded-lg hover:bg-[#008A8D]/10 shrink-0"
                        data-testid="copy-link-btn">
                  {copied.link ? 'Copie !' : 'Copier'}
                </button>
              </div>
              <p className="text-xs text-gray-500 italic mt-1">
                Au premier clic, le client choisit son mot de passe et est connecte automatiquement.
              </p>
            </div>
            <button onClick={onClose} className="w-full px-4 py-2 bg-[#008A8D] text-white rounded-lg font-semibold hover:bg-[#006B6D]" data-testid="client-access-done">
              Fermer
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-semibold mb-2">Compte client active</p>
              <p className="text-xs text-green-700">{result.email_sent ? "Les identifiants ont ete envoyes par email." : "(L'envoi par email a ete desactive ou a echoue.)"}</p>
            </div>
            <div className="p-4 bg-[#FDF8F0] border border-[#E8DCC4] rounded-lg space-y-2">
              <p className="text-xs font-bold text-[#A57F4F] uppercase tracking-wide mb-1">Identifiants ({result.email_sent ? 'copie pour vos archives' : 'a transmettre au client'})</p>
              <p className="text-sm"><b>Email :</b> <span className="font-mono">{result.email}</span></p>
              <div className="flex items-center gap-2">
                <p className="text-sm flex-1"><b>Mot de passe :</b> <span className="font-mono bg-white px-2 py-0.5 rounded border border-[#E8DCC4]" data-testid="client-access-password">{result.password}</span></p>
                <button onClick={() => copy('password', result.password)} className="px-3 py-1 text-xs font-semibold border border-[#008A8D] text-[#008A8D] rounded-lg hover:bg-[#008A8D]/10" data-testid="copy-password-btn">
                  {copied.password ? 'Copie !' : 'Copier'}
                </button>
              </div>
              <p className="text-xs text-gray-500 italic mt-2">Le mot de passe ne pourra plus etre recupere. Le client peut le modifier apres connexion.</p>
            </div>
            <button onClick={onClose} className="w-full px-4 py-2 bg-[#008A8D] text-white rounded-lg font-semibold hover:bg-[#006B6D]" data-testid="client-access-done">
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateClientAccessModal;

